import { useState } from 'react';
import { Link } from 'react-router-dom';
import { localDeals } from '../../../mocks/deals';

interface DealsGridProps {
  activeCategory: string;
  searchQuery: string;
}

export default function DealsGrid({
  activeCategory,
  searchQuery,
}: DealsGridProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('popular');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const nonFeatured = localDeals.filter((d) => !d.featured);

  let filtered = nonFeatured;

  if (activeCategory !== 'all') {
    filtered = filtered.filter((d) => d.category === activeCategory);
  }

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (d) =>
        d.businessName.toLowerCase().includes(q) ||
        d.dealTitle.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q)
    );
  }

  if (sortBy === 'discount') {
    filtered = [...filtered].sort(
      (a, b) => b.discountPercent - a.discountPercent
    );
  } else if (sortBy === 'rating') {
    filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'popular') {
    filtered = [...filtered].sort((a, b) => b.claimed - a.claimed);
  }

  const handleCopy = async (code: string) => {
    try {
      if (!navigator?.clipboard?.writeText) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } else {
        await navigator.clipboard.writeText(code);
      }
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy coupon code:', err);
    }
  };

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'discount', label: 'Biggest Discount' },
    { value: 'rating', label: 'Highest Rated' },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">All Local Deals</h2>
            <p className="text-gray-500 text-sm mt-1">{filtered.length} deals available</p>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-sort-desc text-base"></i>
              {sortOptions.find((o) => o.value === sortBy)?.label}
              <i className="ri-arrow-down-s-line text-base"></i>
            </button>
            {showSortDropdown && (
              <div className="absolute right-0 top-11 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setSortBy(opt.value);
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors ${
                      sortBy === opt.value
                        ? 'bg-teal-50 text-teal-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-gray-400 text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No deals found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or category filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((deal) => (
              <div
                key={deal.id}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group"
              >
                <Link to={`/deals/${deal.id}`} className="flex cursor-pointer">
                  <div className="relative w-36 h-36 flex-shrink-0 overflow-hidden">
                    <img
                      src={deal.image}
                      alt={`${deal.dealTitle} at ${deal.businessName} Roseville`}
                      title={`${deal.businessName} coupon Roseville CA`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-2 left-2">
                      <span className="px-2 py-1 bg-red-500 text-white text-[10px] font-bold rounded-md">
                        {deal.discountPercent === 100
                          ? 'FREE'
                          : `${deal.discountPercent}% OFF`}
                      </span>
                    </div>
                    {deal.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                          FEATURED
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-sm font-bold text-gray-900 truncate group-hover:text-teal-700 transition-colors">{deal.dealTitle}</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-2 truncate">{deal.businessName}</p>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{deal.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-1.5">
                        {deal.dealPrice > 0 ? (
                          <>
                            <span className="text-base font-bold text-teal-700">
                              ${deal.dealPrice}
                            </span>
                            <span className="text-xs text-gray-400 line-through">
                              ${deal.originalPrice}
                            </span>
                          </>
                        ) : (
                          <span className="text-base font-bold text-teal-700">FREE</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs font-semibold text-gray-700">{deal.rating}</span>
                      </div>
                    </div>
                  </div>
                </Link>

                <div className="px-4 pb-4 pt-0">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <i className="ri-scissors-cut-line text-teal-600 text-xs"></i>
                      <span className="text-xs font-mono font-bold text-gray-800 tracking-wide">
                        {deal.couponCode}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopy(deal.couponCode)}
                      className="whitespace-nowrap px-3 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                    >
                      {copiedCode === deal.couponCode ? (
                        <span className="flex items-center gap-1">
                          <i className="ri-check-line"></i> Copied
                        </span>
                      ) : (
                        'Get Code'
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      Expires: {deal.expiresAt}
                    </p>
                    <p className="text-[10px] text-gray-400 flex items-center gap-1">
                      <i className="ri-group-line"></i>
                      {deal.claimed} claimed
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
